import React, { useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import { MainContainer, WarningMessageContainerStyled, WarningMessageTitleStyled } from '../../../layouts/mobile-layout/styled'
import { environmentService } from '../../../providers/environment/EnvironmentService'
import { SessionContext } from '../../../providers/session/SessionContext'
import { constructUrl } from '../../../utils/Utils'
import { HorizontalNonLienarStepper } from '../../stepper/Stepper'
import { EndpointDTO, TableRowDTO } from '../../types'
import ActionDialog from '../action-dialog/ActionDialog'
import { InfoMessageStyled } from '../gusl-table/inline-edit/styled'
import { cleanUpGuslForm, guslFormFieldChange, GuslFormState, postFormData, UpdateDataResponse } from '../maintain-form/guslFormSlice'
import MaintainForm from '../maintain-form/MaintainForm'
import { ErrorUserKycMessageWrapperStyled } from '../maintain-form/styled'
import { maintainTableService } from '../maintain-table/MaintainTableService'
import { ButtonsContainerStyled, RadioButtonContainerStyled, RadioButtonIconStyled, RadioButtonLabelStyled } from '../risk-gain/styled'

interface UserKycProps {
    showUserKyc: boolean
    onClose: () => void
    onOpenTermsAndConditions?: (
        policyCode: string,
        title: string,
        requireAcceptance: boolean,
        textAccept: string,
        onAccept?: (hasAccepted: boolean, code: string) => void
    ) => void
}

interface RadioOption {
    value: string
    label: string
}

interface ExtraProps {
    options: RadioOption[]
    default: string
    group: 'payment' | 'personal'
}

const stepsList = [
    {
        label: 'Personal Information',
        section: 'PersonalInformationBO',
    },
    {
        label: 'Personal Information',
        section: 'CompanyInformationBO',
    },
    {
        label: 'Customer Identification',
        section: 'CustomerIdentificationBO',
    },
    {
        label: 'Customer Identification',
        section: 'CompanyDocumentsBO',
    },
    {
        label: 'Proof of Address',
        section: 'ProofOfAddressBO',
    },
    {
        label: 'Payment Information',
        section: 'BankInformationBO',
    },
    {
        label: 'Payment Information',
        section: 'PaymentInformationBO',
    },
    {
        label: 'Payment Information',
        section: 'ProofOfEWalletAddressBO',
    },
    {
        label: 'Politically Exposed Person',
        section: 'PoliticallyExposedPersonBO',
    },
    {
        label: 'Terms & Conditions',
        section: 'TermsAndConditionsBO',
    },
    {
        label: 'Acknowledges',
        section: 'AcknowledgesBO',
    },
]

export const UserKyc: React.FC<UserKycProps> = ({ showUserKyc: showSignUp, onClose, onOpenTermsAndConditions }) => {
    const [selectedPaymentButton, setSelectedPaymentButton] = useState<string | undefined>(undefined)
    const [selectedPersonalButton, setSelectedPersonalButton] = useState<string | undefined>(undefined)
    const _guslFormState: GuslFormState = useAppSelector((state) => state.guslFormSlice['new_form'])
    const [currentAbortController, setCurrentAbortController] = useState<AbortController | undefined>(undefined)
    const panel = environmentService.getEnvironment()?.userKycDetails
    const sessionContext = React.useContext(SessionContext)
    const [errorMessage, setErrorMessage] = useState<string | undefined>()
    // missingFields grouped by section name
    const [missingFields, setMissingFields] = useState<Record<string, string[]>>({})
    const [showMissing, setShowMissing] = useState<boolean>(false)
    const dispatch = useAppDispatch()
    const [readTermsAndConditions, setReadTermsAndConditions] = useState(false)
    const [readPepDefinition, setReadPepDefinition] = useState(false)
    const [renderModalPEP, setRenderModalPEP] = useState<boolean>(false)
    const [renderModalTerms, setRenderModalTerms] = useState<boolean>(false)

    const onRadioClick = (e: React.MouseEvent<HTMLElement, MouseEvent>, type: string, group?: 'payment' | 'personal') => {
        if (group === 'payment') {
            setSelectedPaymentButton(type)
        } else if (group === 'personal') {
            setSelectedPersonalButton(type)
        } else {
            // backward compatible fallback (keeps old behaviour)
            if (type === 'bank' || type === 'ewallet') {
                setSelectedPaymentButton(type)
            } else if (type === 'individual' || type === 'company') {
                setSelectedPersonalButton(type)
            }
        }
    }

    const renderRadioButton = (label: string, type: string, group?: 'payment' | 'personal'): React.ReactElement => {
        const active =
            group === 'payment'
                ? selectedPaymentButton === type
                : group === 'personal'
                ? selectedPersonalButton === type
                : selectedPaymentButton === type || selectedPersonalButton === type

        return (
            <RadioButtonContainerStyled key={`${group ?? 'g'}_${type}`}>
                <RadioButtonIconStyled
                    active={active}
                    onClick={(e: React.MouseEvent<HTMLElement, MouseEvent>) => onRadioClick(e, type, group)}
                />
                <RadioButtonLabelStyled onClick={(e: React.MouseEvent<HTMLElement, MouseEvent>) => onRadioClick(e, type, group)}>
                    {label}
                </RadioButtonLabelStyled>
            </RadioButtonContainerStyled>
        )
    }

    const renderSignUpRadioButton = (extraProps: ExtraProps): React.ReactElement => {
        return (
            <ButtonsContainerStyled style={{ marginBottom: 16 }}>
                <RadioButtonContainerStyled>
                    {extraProps.options &&
                        extraProps.options.map((option: RadioOption) => renderRadioButton(option.label, option.value, extraProps.group))}
                </RadioButtonContainerStyled>
            </ButtonsContainerStyled>
        )
    }

    const handledOnAccept = (hasAccepted: boolean, code: string) => {
        if (code === 'fe_terms') {
            setReadTermsAndConditions(true)
            setRenderModalTerms(false)
        } else if (code === 'fe_pep') {
            setReadPepDefinition(true)
            setRenderModalPEP(false)
        }
    }

    const openTermsAndConditions = (policyCode: string) => {
        const title = policyCode === 'fe_terms' ? 'Terms and Conditions' : 'Politically Exposed Person (PEP)'
        const requireAcceptance = policyCode === 'fe_terms' || policyCode === 'fe_pep'
        const textAccept =
            policyCode === 'fe_terms'
                ? 'I confirm that I have read and understood these Terms & Conditions'
                : 'I confirm that I have read and understood the definition of a Politically Exposed Person (PEP).'

        if (onOpenTermsAndConditions) {
            onOpenTermsAndConditions(policyCode, title, requireAcceptance, textAccept, handledOnAccept)
        }
    }

    const handleComplete = () => {
        dispatch(
            cleanUpGuslForm({
                code: 'new_form',
            })
        )
        onClose()
    }

    const validateForm = (): boolean => {
        const bySection: Record<string, string[]> = {}
        let totalMissing = 0

        if (_guslFormState) {
            const formData = { ..._guslFormState.formData }
            _guslFormState.fields.forEach((field) => {
                const props = typeof field?.properties === 'string' ? JSON.parse(field?.properties) : field?.properties

                if (!props) return
                if (props.required === false || props.required === undefined) return

                const name = field?.name
                const label = field?.label || name || 'Unnamed field'

                // resolve properties.section robustly (handles JSON string or object)
                let sectionName = 'General'
                try {
                    if (props && props.section) {
                        if (Array.isArray(props.section)) sectionName = String(props.section[0])
                        else sectionName = String(props.section)
                    }
                } catch {
                    sectionName = 'General'
                }

                if (sectionName === 'CompanyInformationBO' && selectedPersonalButton === 'individual') {
                    return
                } else if (sectionName === 'PersonalInformationBO' && selectedPersonalButton === 'company') {
                    return
                } else if (sectionName === 'BankInformationBO' && selectedPaymentButton === 'ewallet') {
                    return
                } else if (sectionName === 'ProofOfEWalletAddressBO' && selectedPaymentButton === 'bank') {
                    return
                } else if (sectionName === 'CompanyDocumentsBO' && selectedPersonalButton === 'individual') {
                    return
                }

                const hasValue =
                    formData &&
                    typeof name !== 'undefined' &&
                    formData[name] !== undefined &&
                    formData[name] !== null &&
                    formData[name] !== ''

                if (!hasValue) {
                    const stepLabel = stepsList.find((x) => x.section === sectionName)?.label || sectionName
                    if (!bySection[stepLabel]) bySection[stepLabel] = []
                    bySection[stepLabel].push(label)
                    totalMissing++
                }
            })
        }

        if (totalMissing > 0) {
            setMissingFields(bySection)
            setErrorMessage(`Please complete the following ${totalMissing} required field(s).`)
            setShowMissing(true)
            return false
        }

        // clear any previous errors
        setMissingFields({})
        setErrorMessage(undefined)
        setShowMissing(false)
        return true
    }

    const performInsert = () => {
        const isValid = validateForm()
        if (panel?.url && isValid) {
            const abortController = new AbortController()
            setCurrentAbortController(abortController)

            const endpoint: EndpointDTO = {
                url: constructUrl(panel?.url, _guslFormState.formData, _guslFormState.rowData),
                method: 'POST',
            }

            const formData = { ..._guslFormState.formData }
            if (formData.rows) delete formData.rows

            let payload = new FormData()
            const regularData: any = {}
            Object.keys(formData).forEach((key) => {
                const value = formData[key]
                if (value instanceof File) {
                    payload.append('files', value, value.name)
                    regularData[`${key}_originalFieldName`] = key
                    regularData[`${key}_fileName`] = value.name
                } else {
                    regularData[key] = value
                }
            })

            regularData['personalInformationType'] = selectedPersonalButton === 'company' ? 'COMPANY' : 'INDIVIDUAL'
            regularData['paymentInformationType'] = selectedPaymentButton === 'bank' ? 'BANK' : 'CRYPTO'

            // Add the data object as JSON string
            payload.append('data', JSON.stringify(regularData))

            dispatch(
                postFormData({
                    code: 'new_form',
                    endpoint: endpoint,
                    sessionContext: sessionContext,
                    abortController: abortController,
                    formData: payload,
                    multipartForm: panel?.multipartForm ?? false,
                })
            )
                .unwrap()
                .then((reply: UpdateDataResponse) => {
                    handleComplete()
                })
                .catch((error) => {
                    console.error('Error during user KYC insertion:', error)
                })
                .finally(() => {
                    setCurrentAbortController(undefined)
                })
        }
    }

    const renderErrorMessage = (): React.ReactElement => {
        const sections = Object.keys(missingFields)
        const totalMissing = sections.reduce((acc, s) => acc + (missingFields[s]?.length || 0), 0)
        if (!errorMessage && totalMissing === 0) return <></>

        const copyGroupedToClipboard = async () => {
            try {
                const parts: string[] = []
                sections.forEach((s) => {
                    const fields = missingFields[s] || []
                    parts.push(`${s}: ${fields.join(', ')}`)
                })
                await navigator.clipboard.writeText(parts.join('\n'))
            } catch {
                // ignore clipboard failures silently
            }
        }

        return (
            <>
                <div style={{ margin: '12px 0' }}>
                    <hr style={{ border: 'none', borderTop: '1px solid #e9ecef', margin: 0 }} />
                </div>

                <ErrorUserKycMessageWrapperStyled>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <strong>{errorMessage ?? 'There are required fields missing.'}</strong>
                            <span style={{ background: '#ffe9e9', color: '#a30000', padding: '2px 8px', borderRadius: 12, fontSize: 12 }}>
                                {totalMissing} missing
                            </span>
                        </div>
                        {totalMissing > 0 && (
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button
                                    type="button"
                                    onClick={() => setShowMissing((s) => !s)}
                                    style={{ background: 'none', border: 'none', color: '#007bff', cursor: 'pointer', padding: 0 }}
                                >
                                    {showMissing ? 'Hide details' : 'Show details'}
                                </button>
                                <button
                                    type="button"
                                    onClick={copyGroupedToClipboard}
                                    style={{ background: 'none', border: 'none', color: '#007bff', cursor: 'pointer', padding: 0 }}
                                    title="Copy missing fields grouped by section"
                                >
                                    Copy
                                </button>
                            </div>
                        )}
                    </div>

                    {showMissing && totalMissing > 0 && (
                        <div style={{ marginTop: 8, maxHeight: 300, overflow: 'auto' }}>
                            {sections.map((section) => (
                                <div key={section} style={{ marginBottom: 10 }}>
                                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{section}</div>
                                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                                        {(missingFields[section] || []).map((f, idx) => (
                                            <li key={idx} style={{ fontSize: 13 }}>
                                                {f}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    )}
                </ErrorUserKycMessageWrapperStyled>
            </>
        )
    }

    const renderErrorField = (panelName: string): React.ReactElement => {
        if (renderModalPEP && panelName === 'PoliticallyExposedPersonBO') {
            return (
                <ErrorUserKycMessageWrapperStyled style={{ fontWeight: 'bold', fontSize: 13 }}>
                    * Read the definition of{' '}
                    <button
                        type="button"
                        onClick={(e) => {
                            e.preventDefault()
                            openTermsAndConditions('fe_pep')
                        }}
                        style={{
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            background: 'none',
                            border: 'none',
                            color: '#007bff',
                            font: 'inherit',
                            padding: 0,
                        }}
                    >
                        PEP
                    </button>{' '}
                    to sign the documents
                </ErrorUserKycMessageWrapperStyled>
            )
        } else if (renderModalTerms && panelName === 'TermsAndConditionsBO') {
            return (
                <ErrorUserKycMessageWrapperStyled style={{ fontWeight: 'bold', fontSize: 13 }}>
                    * Read the{' '}
                    <button
                        type="button"
                        onClick={(e) => {
                            e.preventDefault()
                            openTermsAndConditions('fe_terms')
                        }}
                        style={{
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            background: 'none',
                            border: 'none',
                            color: '#007bff',
                            font: 'inherit',
                            padding: 0,
                        }}
                    >
                        Terms and Conditions
                    </button>{' '}
                    to sign the documents
                </ErrorUserKycMessageWrapperStyled>
            )
        }
        return <></>
    }

    const getMaintainFormComponent = (panelName: string) => {
        const action = environmentService.getEnvironment()?.userKycDetails
        const isPaymentStep = panelName === 'PaymentInformationBO'
        const paymentMap: Record<string, string> = {
            bank: 'BankInformationBO',
            ewallet: 'ProofOfEWalletAddressBO',
        }
        const selectedPaymentPanel = isPaymentStep ? paymentMap[selectedPaymentButton || ''] : panelName

        const extraProps: ExtraProps = isPaymentStep
            ? {
                  options: [
                      { value: 'bank', label: 'Bank Account' },
                      { value: 'ewallet', label: 'Crypto' },
                  ],
                  default: 'bank',
                  group: 'payment' as const,
              }
            : {
                  options: [],
                  default: '',
                  group: 'personal' as const,
              }

        const renderPaymentSection = () => {
            if (!isPaymentStep) return null
            return renderSignUpRadioButton(extraProps)
        }

        const isPersonalStep = panelName === 'PersonalInformationBO'
        const personalMap: Record<string, string> = {
            company: 'CompanyInformationBO',
            individual: 'PersonalInformationBO',
        }

        const selectedPersonPanel = isPersonalStep ? personalMap[selectedPersonalButton || ''] : panelName
        const extraPersonalProps: ExtraProps = isPersonalStep
            ? {
                  options: [
                      { value: 'individual', label: 'Individual' },
                      { value: 'company', label: 'Company' },
                  ],
                  default: 'individual',
                  group: 'personal' as const,
              }
            : {
                  options: [],
                  default: '',
                  group: 'personal' as const,
              }

        const renderPersonSection = () => {
            if (!isPersonalStep) return null
            return renderSignUpRadioButton(extraPersonalProps)
        }

        const renderTerms = () => {
            return (
                <div>
                    <InfoMessageStyled>
                        <ol
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                paddingLeft: 60,
                                margin: 0,
                                gap: 14,
                                listStyleType: 'decimal',
                                listStylePosition: 'outside',
                            }}
                        >
                            <li>
                                The Customer has carefully read, fully understood, and expressly agreed to be bound by the Terms and
                                Conditions of Supernova Trading Ltd. {/* clickable link opens the terms modal/offcanvas */}
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.preventDefault()
                                        openTermsAndConditions('fe_terms')
                                    }}
                                    style={{
                                        textDecoration: 'underline',
                                        cursor: 'pointer',
                                        background: 'none',
                                        border: 'none',
                                        color: '#007bff',
                                        font: 'inherit',
                                        padding: 0,
                                    }}
                                >
                                    Terms and Conditions
                                </button>{' '}
                                (read to sign) I confirm that: .
                            </li>
                            <li>
                                The Customer acknowledges that the Terms and Conditions, together with this acknowledgment, form a binding
                                legal agreement between the Customer and Supernova Trading Ltd.
                            </li>
                            <li>Where the Customer is an individual, this acknowledgment has been signed personally by the Customer.</li>
                            <li>
                                Where the Customer is a corporation, partnership, or other legal entity, this acknowledgment has been
                                executed and accepted by a duly authorized director, officer, or representative of the Customer with proper
                                signing authority.
                            </li>
                        </ol>
                    </InfoMessageStyled>
                </div>
            )
        }

        const renderAcknowledgment = () => {
            return (
                <div>
                    <InfoMessageStyled>
                        <ol
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 14,
                                paddingLeft: 60,
                                marginBottom: 16,
                                listStyleType: 'decimal',
                                listStylePosition: 'outside',
                            }}
                        >
                            <li>
                                All information, documents, and materials provided by the Customer on or through the Supernova Trading Ltd.
                                website or Platform are, to the best of the Customer's knowledge, accurate, complete, and truthful at the
                                time of submission.
                            </li>
                            <li>
                                The Customer undertakes to promptly update and correct such information, documents, and materials as may be
                                necessary to ensure their continuing accuracy and completeness.
                            </li>
                            <li>
                                The Customer acknowledges and agrees that its access to and use of the Supernova Trading Ltd. website or
                                Platform is subject to and governed by the Terms and Conditions of Supernova Trading Ltd., which the
                                Customer has read, understood, and accepted.
                            </li>
                            <li>
                                In the case where the Customer is a corporate or other legal entity, this Acknowledgment, together with the
                                Terms and Conditions, has been executed and accepted by a duly authorized director, officer, or other
                                representative of the Customer with appropriate signing authority.
                            </li>
                        </ol>
                    </InfoMessageStyled>
                </div>
            )
        }

        const renderPEP = () => {
            return (
                <div>
                    <InfoMessageStyled>
                        <div
                            style={{
                                listStyleType: 'decimal',
                                listStylePosition: 'outside',
                            }}
                        >
                            <p>
                                Having read and understood the definition of{' '}
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.preventDefault()
                                        openTermsAndConditions('fe_pep')
                                    }}
                                    style={{
                                        textDecoration: 'underline',
                                        cursor: 'pointer',
                                        background: 'none',
                                        border: 'none',
                                        color: '#007bff',
                                        font: 'inherit',
                                        padding: 0,
                                    }}
                                >
                                    PEP
                                </button>{' '}
                                (read to sign) I confirm that:
                            </p>
                        </div>
                    </InfoMessageStyled>
                </div>
            )
        }

        const renderCompanyDocuments = () => {
            return (
                <div>
                    <InfoMessageStyled>
                        <div className="mb-4">
                            <p>Customer must provide:</p>
                        </div>
                    </InfoMessageStyled>
                </div>
            )
        }

        // if payment step and nothing selected, show only the payment radios
        if (isPaymentStep && !selectedPaymentButton) {
            return <React.Fragment key={panelName + '-fragment'}>{renderPaymentSection()}</React.Fragment>
        }
        // if personal step and nothing selected, show only the person radios
        if (isPersonalStep && !selectedPersonalButton) {
            return <React.Fragment key={panelName + '-fragment'}>{renderPersonSection()}</React.Fragment>
        }

        // base target panel
        const targetPanelName = isPaymentStep ? selectedPaymentPanel : isPersonalStep ? selectedPersonPanel : panelName

        // special case: when the requested panel is CustomerIdentificationBO
        // show CompanyDocumentsBO if user selected 'company', otherwise CustomerIdentificationBO
        const effectivePanelName =
            panelName === 'CustomerIdentificationBO'
                ? selectedPersonalButton === 'company'
                    ? 'CompanyDocumentsBO'
                    : 'CustomerIdentificationBO'
                : targetPanelName

        const filteredRows =
            action?.rows?.map((row: any) => ({
                ...row,
                columns: row.columns.map((col: any) => ({
                    ...col,
                    fields: col.fields.filter((field: any) => {
                        try {
                            const props = field.properties ? JSON.parse(field.properties) : {}
                            return props.section === effectivePanelName
                        } catch {
                            return false
                        }
                    }),
                })),
            })) ?? []

        if (!action) return null

        let formData: any = _guslFormState?.formData ?? {}
        if (action?.rows) {
            formData = { ...formData, rows: Object.values(filteredRows) }
        }

        if (readPepDefinition === false && effectivePanelName === 'PoliticallyExposedPersonBO') {
            if (formData.notPep && formData.notPep !== false) {
                dispatch(
                    guslFormFieldChange({
                        code: 'new_form',
                        name: 'notPep',
                        value: false,
                        overwrite: true,
                        panelName: 'PoliticallyExposedPersonBO',
                    })
                )
                setRenderModalPEP(true)
            }

            if (formData.pep && formData.pep !== false) {
                dispatch(
                    guslFormFieldChange({
                        code: 'new_form',
                        name: 'pep',
                        value: false,
                        overwrite: true,
                        panelName: 'PoliticallyExposedPersonBO',
                    })
                )
                setRenderModalPEP(true)
            }

            if (formData.customerFurtherConfirms && formData.customerFurtherConfirms !== false) {
                dispatch(
                    guslFormFieldChange({
                        code: 'new_form',
                        name: 'customerFurtherConfirms',
                        value: false,
                        overwrite: true,
                        panelName: 'PoliticallyExposedPersonBO',
                    })
                )
                setRenderModalPEP(true)
            }
        }

        if (readTermsAndConditions === false && effectivePanelName === 'TermsAndConditionsBO') {
            if (formData.termsAndConditions && formData.termsAndConditions !== false) {
                dispatch(
                    guslFormFieldChange({
                        code: 'new_form',
                        name: 'termsAndConditions',
                        value: false,
                        overwrite: true,
                        panelName: 'TermsAndConditionsBO',
                    })
                )
                setRenderModalTerms(true)
            }
        }

        return (
            <React.Fragment key={effectivePanelName + '-fragment'}>
                {renderPersonSection()}
                {renderPaymentSection()}
                {effectivePanelName === 'CompanyDocumentsBO' && renderCompanyDocuments()}
                {effectivePanelName === 'PoliticallyExposedPersonBO' && renderPEP()}
                <MaintainForm
                    isDialog={true}
                    hideActionBar={true}
                    controlOnTop={false}
                    canEdit={maintainTableService.getCanEditForAction(action)}
                    formMode={maintainTableService.getFormModeForAction(action)}
                    data={formData}
                    rowData={filteredRows}
                    menuItem={maintainTableService.convertToMenu({ id: '1' } as TableRowDTO, action)}
                    minModalWidth={'95%'}
                    refreshTable={() => {
                        /* your refresh logic */
                    }}
                    closeDialog={onClose}
                >
                    {effectivePanelName === 'TermsAndConditionsBO' && renderTerms()}
                    {effectivePanelName === 'AcknowledgesBO' && renderAcknowledgment()}
                </MaintainForm>
                {renderErrorMessage()}
                {renderErrorField(effectivePanelName)}
            </React.Fragment>
        )
    }

    const getSignUpSteps = () => {
        return [
            {
                label: 'Personal Information',
                component: getMaintainFormComponent('PersonalInformationBO') || <div>No Personal Information Form Available</div>,
            },
            {
                label: 'Customer Identification',
                component: getMaintainFormComponent('CustomerIdentificationBO') || <div>No Customer Identification Form Available</div>,
            },
            {
                label: 'Proof of Address',
                component: getMaintainFormComponent('ProofOfAddressBO') || <div>No Proof of Address Form Available</div>,
            },
            {
                label: 'Payment Information',
                component: getMaintainFormComponent('PaymentInformationBO') || <div>No Payment Information Form Available</div>,
            },
            {
                label: 'Politically Exposed Person',
                component: getMaintainFormComponent('PoliticallyExposedPersonBO') || (
                    <div>No Politically Exposed Person Form Available</div>
                ),
            },
            {
                label: 'Terms & Conditions',
                component: getMaintainFormComponent('TermsAndConditionsBO') || <div>No Terms & Conditions Form Available</div>,
            },
            {
                label: 'Acknowledges',
                component: getMaintainFormComponent('AcknowledgesBO') || <div>No Acknowledges Form Available</div>,
            },
        ]
    }

    const renderSignUpPanel = (): React.ReactElement | null => {
        const steps = getSignUpSteps()

        function handleSignUpComplete(data: any): void {
            if (panel?.url) {
                performInsert()
            }
        }

        return (
            <>
                {showSignUp && (
                    <ActionDialog action={environmentService.getEnvironment()?.userKycDetails} cannotClose={true} closeDialog={onClose}>
                        <HorizontalNonLienarStepper stepDefinitions={steps} onComplete={handleSignUpComplete} />
                    </ActionDialog>
                )}
            </>
        )
    }

    return renderSignUpPanel()
}

export default UserKyc
